import axios from "axios";
import React, { useContext, useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import UserContext from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import authenticate from "../../utils/Authenticate";
import displayError from "../../utils/ErrorHandler";

const CreateDish = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    units: "unit",
    cookingQty: "",
    cookingUnit: "",
  });
  const [ingredients, setIngredients] = useState([
    { id: Date.now(), name: "", quantity: "", unit: "" },
  ]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "" });

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, navigate, setUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleIngredientChange = (index, field, value) => {
    setIngredients((prevIngredients) =>
      prevIngredients.map((ingredient, i) =>
        i === index ? { ...ingredient, [field]: value,unit:value.unit } : ingredient
      )
    );
  };

  const addIngredientField = () => {
    setIngredients((prevIngredients) => [
      ...prevIngredients,
      { id: Date.now(), name: "", quantity: "", unit: "" },
    ]);
  };

  const removeIngredientField = (index) => {
    setIngredients((prevIngredients) =>
      prevIngredients.filter((_, i) => i !== index)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dishData = {
      ...formData,
      ingredients: ingredients.map(({ id, name, quantity, unit }) => ({
        name,
        quantity,
        unit,
      })),
    };
    try {
      // Replace with your actual API endpoint
      const res = await axios.post("/api/dish/createdish", dishData, {withCredentials:true});
      setNotification({ message: "Dish created successfully!", type: "success" });
      console.log(res.data);
      // Optionally reset form or redirect
    } catch (error) {
      setNotification({ message: "Failed to create dish.", type: "error" });
      console.error(error);
    }
  };

  const loadOptions = async (inputValue, callback) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/findItems`,
        {
          params: { q: inputValue },
        }
      );
      const options = response.data.map((record) => ({
        value: record._id,
        unit: record.unit,
        label: record.name,
      }));
      callback(options);
    } catch (error) {
      console.error("Error loading options:", error);
    }
  };

  return (
    <div className="wrapper">
      <div className="w-full">
        <h2 className="text-base font-bold bg-lime-800 text-white p-3 py-4 text-center">
          Create Dish
        </h2>
        {notification.message && (
          <div
            className={`p-2 mb-4 ${
              notification.type === "error"
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {notification.message}
          </div>
        )}
      </div>
      <div className="p-4 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit}>
          {/* Dish Name + Cooking Qty + Unit */}
          <div className="grid grid-cols-12 gap-4 mb-4 md:flex md:space-x-2">
            {/* Dish Name */}
            <div className="col-span-6 md:w-full">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                placeholder="Enter dish name"
                required
              />
            </div>
            {/* Cooking Quantity */}
            <div className="col-span-3 md:w-1/3">
              <input
                type="number"
                name="cookingQty"
                value={formData.cookingQty}
                onWheel={(e) => e.target.blur()}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                placeholder="Qty"
                required
              />
            </div>
            {/* Cooking Unit */}
            <div className="col-span-3 md:w-1/3">
              <select
                name="cookingUnit"
                value={formData.cookingUnit || ""}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                required
              >
                <option value="" disabled>
                  Select unit
                </option>
                <option value="kg">kg</option>
                <option value="litre">litre</option>
                <option value="nos">nos</option>
                <option value="grams">grams</option>
              </select>
            </div>
          </div>

          {/* Ingredients Section */}
          <h3 className="font-semibold text-gray-800 mb-2">Ingredients</h3>
          {ingredients.map((ingredient, index) => (
            <div
              key={ingredient.id}
              className="mb-4 flex flex-col md:flex-row md:space-x-2"
            >
              {/* Ingredient Name */}
              <div className="w-full md:w-1/2 mb-2 md:mb-0">
                <AsyncSelect
                  id={`multi-select-${index}`}
                  loadOptions={loadOptions}
                  onChange={(selected) =>
                    handleIngredientChange(index, "name", selected || "")
                  }
                  value={ingredient.name}
                  placeholder="Search and select ingredient..."
                  className="mt-1 block w-full focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  styles={{
                    control: (base) => ({
                      ...base,
                      background: "white",
                      borderColor: "#D1D5DB",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#93C5FD",
                      },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#9CA3AF",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#BFDBFE",
                      borderRadius: "9999px",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#1E40AF",
                      paddingLeft: "8px",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "#9F1A1B",
                      "&:hover": {
                        backgroundColor: "#FEF2F2",
                        color: "#DC2626",
                      },
                    }),
                  }}
                />
              </div>

              {/* Quantity */}
              <div className="w-full md:w-1/4 mb-2 md:mb-0">
                <input
                  type="number"
                  value={ingredient.quantity}
                  onChange={(e) =>
                    handleIngredientChange(index, "quantity", e.target.value)
                  }
                  onWheel={(e) => e.target.blur()}
                  className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  placeholder="Qty"
                  required
                />
              </div>

              {/* Unit */}
              <div className="w-full md:w-1/4 mb-2 md:mb-0">
                <input
                  value={ingredient?.unit}
                  type="text"
                  placeholder="Unit"
                  disabled
                  className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  required
                />
              </div>

              {/* Remove Button */}
              <div className="w-full md:w-auto mt-2 md:mt-0">
                {ingredients.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeIngredientField(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add Ingredient Button */}
          <button
            type="button"
            onClick={addIngredientField}
            className="mb-6 inline-flex items-center px-4 py-2 w-full md:w-auto border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
          >
            + Add Ingredient
          </button>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-fmb hover:bg-red-800 focus:outline-none"
          >
            Submit Dish
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateDish;